/**
 * Terminal call status helpers — accidental / unanswered short rings.
 */

/** Matches transcription minimum — calls shorter than this are treated as accidental. */
export const ACCIDENTAL_CALL_MAX_SEC = 5

export type TerminalCallStatus =
  | 'COMPLETED'
  | 'INTERRUPTED'
  | 'MISSED'
  | 'DECLINED'
  | 'BUSY'
  | 'CANCELLED'

export function callDurationSec(startedAt: string, endedAt: string): number | null {
  const start = Date.parse(startedAt)
  const end = Date.parse(endedAt)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null
  return Math.round((end - start) / 1000)
}

/** True when the call never reached a connected/active state. */
export function isUnansweredTerminalStatus(status: string): boolean {
  const normalized = status.toUpperCase()
  return normalized === 'MISSED' || normalized === 'CANCELLED'
}

/**
 * Very short unanswered rings (caller hung up or cancelled quickly) should not
 * appear as missed calls in history, DM tiles, or push notifications.
 */
export function isAccidentalUnansweredCall(
  status: string,
  startedAt: string,
  endedAt: string,
): boolean {
  if (!isUnansweredTerminalStatus(status)) return false
  const duration = callDurationSec(startedAt, endedAt)
  if (duration == null) return false
  return duration < ACCIDENTAL_CALL_MAX_SEC
}

/**
 * Pick END payload reason for an unanswered ring. Short rings become CANCELLED
 * so peers dismiss silently instead of recording a missed call.
 */
export function resolveUnansweredEndReason(
  startedAt: number | undefined,
  endedAt: Date = new Date(),
  fallback: 'MISSED' | 'CANCELLED' = 'MISSED',
): 'MISSED' | 'CANCELLED' {
  if (!startedAt) return fallback
  const duration = callDurationSec(
    new Date(startedAt).toISOString(),
    endedAt.toISOString(),
  )
  if (duration != null && duration < ACCIDENTAL_CALL_MAX_SEC) return 'CANCELLED'
  return fallback
}
